import './ValidatePlacement.scss';
import * as Dfns from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import {
  formatNumber,
  toPrettyPercent2Decimals
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { BPMTable, CellRenderer, Header } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Placement } from '@Components/designConstants';
import { useSetError } from '../../../redux/modals';
import { DailyPlacementData, useValidatePlacement } from './use-validate-placement';

const VALIDATE_PLACEMENT_HEADERS: Header[] = [
  {
    label: 'Date',
    name: 'est_day',
    flex: 1,
    sortAscending: true,
    sortPriority: 0
  },
  {
    label: 'Derived ID',
    name: 'network',
    flex: 1
  },
  {
    label: 'Platform',
    name: 'platform',
    flex: 1
  },
  {
    label: 'ISCI',
    name: 'isci',
    flex: 1
  },
  {
    label: 'Pixel Impression Count',
    name: 'pixel_impression_count',
    flex: 1,
    renderer: (row: DailyPlacementData) => formatNumber(row.pixel_impression_count, 0)
  },
  {
    label: 'App Reported Impression Count (SDI)',
    name: 'app_reported_impression_count',
    flex: 1.25,
    renderer: (row: DailyPlacementData) => formatNumber(row.app_reported_impression_count, 0)
  },
  {
    label: '% Difference',
    name: 'percentage_difference',
    flex: 0.75,
    renderer: (row: DailyPlacementData) =>
      row.percentage_difference == null
        ? '∞'
        : toPrettyPercent2Decimals(Math.abs(row.percentage_difference))
  }
];

const PIXEL_IMPRESSION_COUNT_INDEX = 4;
const PIXEL_IMPRESSION_COUNT_DESCRIPTION =
  'The number of impressions recorded by the BPM Pixel. This figure should be similar (within 5%) of the App Reported Impression Count for historical days.';

const APP_REPORTED_IMPRESSION_COUNT_INDEX = 5;
const APP_REPORTED_IMPRESSION_COUNT_DESCRIPTION =
  'The number of impressions recorded by our invoicing soure of truth (typically our Ad Server) and reflected in App. This figure should be similar (within 5%) of the Pixel Impression Count for historical days.';

const tooltipHeaderRenderer = (label: string, tooltipDescription: string) => (
  <div className='header-label-container'>
    {label}
    <div className='tooltip-container'>
      <InfoTooltip placement={Placement.BOTTOM_END}>{tooltipDescription}</InfoTooltip>
    </div>
  </div>
);

export const ValidatePlacement = () => {
  const setError = useSetError();
  const { data: placementData, isLoading, error } = useValidatePlacement();

  const [minEstDay, maxEstDay] = useMemo(() => {
    if (!placementData?.length) {
      return ['', ''];
    }
    const minIso = Dfns.min(
      placementData.map(row => Dfns.parseISO(row.est_day)).filter(Dfns.isValid)
    );
    const maxIso = Dfns.max(
      placementData.map(row => Dfns.parseISO(row.est_day)).filter(Dfns.isValid)
    );
    return [Dfns.format(minIso, 'M/dd/yy'), Dfns.format(maxIso, 'M/dd/yy')];
  }, [placementData]);

  useEffect(() => {
    if (error) {
      setError({ message: error.message, reportError: error });
    }
  }, [error, setError]);

  const headersRenderer: CellRenderer<Header> = useCallback(({ data, columnIndex }) => {
    const label = String(data);
    if (columnIndex === PIXEL_IMPRESSION_COUNT_INDEX) {
      return tooltipHeaderRenderer(label, PIXEL_IMPRESSION_COUNT_DESCRIPTION);
    }
    if (columnIndex === APP_REPORTED_IMPRESSION_COUNT_INDEX) {
      return tooltipHeaderRenderer(label, APP_REPORTED_IMPRESSION_COUNT_DESCRIPTION);
    }
    return <>{label}</>;
  }, []);

  const renderTableLoading = useCallback(
    () => <TableLoading isLoading={isLoading} error={error} />,
    [isLoading, error]
  );

  return (
    <TablePage dataTestId='validate-placement' tableClassName='ad-ops-table'>
      <BPMTable
        headers={VALIDATE_PLACEMENT_HEADERS}
        headersRenderer={headersRenderer}
        alternateColors
        data={placementData ?? []}
        filterBar
        additionalControls={
          <div className='table-title'>
            Previous Week&apos;s Placement Data ({minEstDay} - {maxEstDay})
          </div>
        }
        noRowsRenderer={renderTableLoading}
      />
    </TablePage>
  );
};
